import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House, BookOpen, User, Books, Scissors, PencilLine,
  CalendarBlank, ChartLineUp, SignOut, Sparkle, Trash, ChatCenteredDots, Lightning, ShieldCheck, CaretDoubleLeft, CaretDoubleRight, List, X,
} from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import GlobalSearch from "@/components/GlobalSearch";
import WaitlistModal from "@/components/WaitlistModal";

const TRIAL_LIMIT = 5;

const allItems = [
  { to: "/app", label: "Dashboard", icon: House, testid: "nav-dashboard", end: true, plans: ["pro","admin"] },
  { to: "/app/kb", label: "Knowledge Base", icon: BookOpen, testid: "nav-kb", plans: ["trial","pro","admin"] },
  { to: "/app/identity", label: "About Me", icon: User, testid: "nav-identity", plans: ["trial","pro","admin"] },
  { to: "/app/storybank", label: "Storybank", icon: Books, testid: "nav-storybank", plans: ["trial","pro","admin"] },
  { to: "/app/pov", label: "POV & Ideas", icon: Lightning, testid: "nav-pov", plans: ["trial","pro","admin"] },
  { to: "/app/comments", label: "Comments", icon: ChatCenteredDots, testid: "nav-comments", plans: ["trial","pro","admin"] },
  { to: "/app/swipes", label: "Swipe File", icon: Scissors, testid: "nav-swipes", plans: ["pro","admin"] },
  { to: "/app/draft", label: "Drafting Studio", icon: PencilLine, testid: "nav-draft", plans: ["trial","pro","admin"] },
  { to: "/app/planner", label: "Planner", icon: CalendarBlank, testid: "nav-planner", plans: ["pro","admin"] },
  { to: "/app/analytics", label: "Analytics", icon: ChartLineUp, testid: "nav-analytics", plans: ["pro","admin"] },
  { to: "/app/trash", label: "Trash", icon: Trash, testid: "nav-trash", plans: ["trial","pro","admin"] },
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin", adminOnly: true, plans: ["admin"] },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const plan = user?.plan || "pro";
  const isAdmin = user?.role === "admin";
  const items = allItems.filter(i => {
    if (i.adminOnly) return isAdmin;
    return i.plans.includes(plan);
  });
  const draftsUsed = user?.drafts_used || 0;
  const draftsLeft = Math.max(0, TRIAL_LIMIT - draftsUsed);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bl_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem("bl_sidebar_collapsed", collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-soft)" }}>
      <aside className={`shrink-0 hidden lg:flex flex-col p-4 transition-all duration-200 ${collapsed ? "w-[76px]" : "w-[248px]"}`} style={{ background: "var(--bg-soft)" }}>
        <div className="card p-4 mb-3 flex items-center justify-between gap-2">
          <Link to={plan === "trial" ? "/app/draft" : "/app"} className="flex items-center gap-2 min-w-0" data-testid="brand-home">
            <span className="inline-block w-7 h-7 rounded-full shrink-0" style={{ background: "var(--grad-warm)" }} />
            {!collapsed && <span className="text-lg font-semibold tracking-tight truncate">BrandLoop</span>}
          </Link>
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(v => !v)}
            className="p-1.5 rounded-full hover:bg-[color:var(--paper)] shrink-0"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{color:'var(--ink-3)'}}
          >
            {collapsed ? <CaretDoubleRight size={13}/> : <CaretDoubleLeft size={13}/>}
          </button>
        </div>

        {plan === "trial" && !collapsed && (
          <div className="card p-4 mb-3" style={{ background: "var(--accent-tint)", borderColor: "transparent" }} data-testid="trial-banner">
            <div className="flex items-center gap-2 eyebrow" style={{ color: "var(--accent-2)" }}>
              <Sparkle size={11} weight="fill" /> Free trial
            </div>
            <div className="text-3xl font-semibold mt-2" data-testid="trial-drafts-left">
              {draftsLeft}<span className="text-sm ml-1 font-normal" style={{color:'var(--ink-3)'}}>/{TRIAL_LIMIT} drafts</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.6)" }}>
              <div style={{ background: "var(--accent)", width: `${(draftsUsed/TRIAL_LIMIT)*100}%`, height: "100%", transition: "width 400ms ease" }} />
            </div>
            <button data-testid="trial-upgrade-btn" onClick={()=>setShowWaitlist(true)} className="btn btn-accent btn-sm w-full mt-3">
              Upgrade to Pro
            </button>
          </div>
        )}

        <WaitlistModal open={showWaitlist} onClose={() => setShowWaitlist(false)} />

        <nav className="flex-1 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              title={collapsed ? it.label : ""}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-full transition-colors ${
                  isActive ? "font-medium" : "hover:bg-white"
                } ${collapsed ? "justify-center" : ""}`
              }
              style={({ isActive }) => isActive ? { background: "var(--ink)", color: "#fff" } : { color: "var(--ink-2)" }}
            >
              <it.icon size={17} weight="duotone" />
              {!collapsed && <span>{it.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!collapsed && (
          <div className="mt-3">
            <GlobalSearch />
          </div>
        )}

        <div className="card p-3 mt-3">
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white shrink-0" style={{ background: "var(--grad-warm)" }}>
              {(user?.name || user?.email || "?").slice(0,1).toUpperCase()}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate font-medium" data-testid="user-name">{user?.name || user?.email}</div>
                  <div className="text-xs truncate" style={{ color: "var(--ink-3)" }}>{user?.email}</div>
                </div>
                <button data-testid="logout-btn" onClick={handleLogout} className="p-2 rounded-full hover:bg-[color:var(--paper)]" title="Sign out">
                  <SignOut size={15} style={{color:'var(--ink-3)'}}/>
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col p-2 lg:p-3 lg:pl-0">
        <div className="lg:hidden flex items-center justify-between px-2 py-3 mb-2">
          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-nav-toggle"
              onClick={()=>setMobileNavOpen(true)}
              className="p-2 rounded-full hover:bg-[color:var(--paper)]"
              aria-label="Open menu"
            ><List size={20}/></button>
            <span className="inline-block w-6 h-6 rounded-full" style={{ background: "var(--grad-warm)" }} />
            <span className="text-base font-semibold">BrandLoop</span>
          </div>
          <div className="flex items-center gap-2">
            {plan === "trial" && <span className="chip chip-accent">{draftsLeft} drafts left</span>}
            <button data-testid="logout-btn-mobile" onClick={handleLogout} className="btn btn-ghost btn-sm">Sign out</button>
          </div>
        </div>

        {/* Mobile off-canvas nav */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-50 lg:hidden"
            style={{ background: "rgba(0,0,0,0.55)" }}
            onClick={()=>setMobileNavOpen(false)}
            data-testid="mobile-nav-overlay"
          >
            <aside
              onClick={(e)=>e.stopPropagation()}
              className="absolute left-0 top-0 bottom-0 w-[280px] p-3 flex flex-col fade-up"
              style={{ background: "var(--bg-soft)" }}
              data-testid="mobile-nav"
            >
              <div className="card p-4 mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-7 h-7 rounded-full" style={{ background: "var(--grad-warm)" }} />
                  <span className="text-lg font-semibold tracking-tight">BrandLoop</span>
                </div>
                <button data-testid="mobile-nav-close" onClick={()=>setMobileNavOpen(false)} className="p-2 rounded-full hover:bg-[color:var(--paper)]"><X size={16}/></button>
              </div>
              <nav className="card p-2 flex-1 overflow-y-auto scroll-thin space-y-1">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      data-testid={`mobile-${it.testid}`}
                      onClick={()=>setMobileNavOpen(false)}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-2xl text-[15px] ${isActive ? "font-semibold" : ""}`}
                      style={({ isActive }) => isActive ? { background: "var(--accent-tint)", color: "var(--accent-2)" } : { color: "var(--ink-2)" }}
                    >
                      <Icon size={18}/> {it.label}
                    </NavLink>
                  );
                })}
              </nav>
              {plan === "trial" && (
                <button
                  onClick={()=>{ setShowWaitlist(true); setMobileNavOpen(false); }}
                  className="btn btn-accent mt-3 justify-center"
                  data-testid="mobile-upgrade"
                >Upgrade to Pro</button>
              )}
            </aside>
          </div>
        )}

        <div className="card flex-1 overflow-auto scroll-thin" style={{ background: "#fff" }}>
          <div className={`w-full ${collapsed ? "max-w-none" : "max-w-[1440px]"} mx-auto px-4 lg:px-6 py-6 lg:py-14`}>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
